import { Link, useLocation } from 'react-router';
import {
  Building,
  Users,
  LogOut,
  LayoutDashboard,
  UserCog,
  Building2,
  Home,
  Settings,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface SidebarProps {
  role: string | null;
  onLogout: () => void;
}

export function AdminSidebar({ role, onLogout }: SidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;

  const links = [
    {
      href: '/admin',
      label: 'Dashboard',
      icon: Home,
      roles: ['system_admin', 'building_admin', 'organization_admin'],
      exact: true,
    },
    {
      href: '/admin/organizations',
      label: 'Organizations',
      icon: Building2,
      roles: ['system_admin'],
    },
    {
      href: '/admin/buildings',
      label: 'Buildings',
      icon: Building,
      roles: ['system_admin'],
    },
    {
      href: '/admin/admins',
      label: 'Admins',
      icon: UserCog,
      roles: ['system_admin'],
    },
    {
      href: '/admin/residents',
      label: 'Residents',
      icon: Users,
      roles: ['system_admin', 'building_admin'],
    },
    {
      href: '/org-admin/buildings',
      label: 'Buildings',
      icon: Building,
      roles: ['organization_admin'],
    },
    {
      href: '/org-admin/units',
      label: 'Units',
      icon: LayoutDashboard,
      roles: ['organization_admin'],
    },
    {
      href: '/org-admin/residents',
      label: 'Residents',
      icon: Users,
      roles: ['organization_admin'],
    },
  ];

  return (
    <aside className="flex h-full w-72 flex-col shrink-0 p-6">
      {/* Logo Section */}
      <div className="mb-10">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-14 h-14 rounded-[1.25rem] bg-[var(--charcoal)] shadow-lg">
            <span className="text-[var(--cream)] font-bold text-xl">U</span>
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-semibold text-[var(--charcoal)] leading-tight">
              UniDom
            </span>
            <span className="text-xs text-[var(--warm-gray)] font-medium">
              Property Manager
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto">
        <p className="px-4 mb-4 text-xs font-semibold text-[var(--warm-gray)] uppercase tracking-wider">
          Menu
        </p>
        <nav className="space-y-2">
          {links.map((link) => {
            if (link.roles && role && !link.roles.includes(role)) return null;

            const Icon = link.icon;
            const isActive = link.exact
              ? pathname === link.href
              : pathname.startsWith(link.href);

            return (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'flex items-center gap-3 px-4 py-3.5 text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-[var(--charcoal)] text-[var(--cream)] rounded-full shadow-lg shadow-black/10'
                    : 'text-[var(--charcoal-light)] hover:bg-[var(--cream-dark)] hover:text-[var(--charcoal)] rounded-full'
                )}
              >
                <Icon className="h-5 w-5 shrink-0" strokeWidth={isActive ? 2.5 : 2} />
                <span>{link.label}</span>
                {isActive && (
                  <ChevronRight className="h-4 w-4 ml-auto opacity-60" />
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Bottom Section */}
      <div className="mt-auto pt-6 space-y-3">
        {/* Settings Link */}
        <Link
          to="/admin/settings"
          className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-[var(--charcoal-light)] hover:bg-[var(--cream-dark)] hover:text-[var(--charcoal)] rounded-full transition-all duration-200"
        >
          <Settings className="h-5 w-5 shrink-0" />
          <span>Settings</span>
        </Link>

        {/* User Card */}
        <div className="p-4 rounded-[1.5rem] bg-[var(--card)] shadow-[var(--card-shadow)] border border-[var(--border)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[var(--mustard)] to-[var(--mustard-light)] flex items-center justify-center shadow-md">
              <UserCog className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[var(--charcoal)] truncate leading-tight">
                Administrator
              </p>
              <p className="text-xs text-[var(--warm-gray)] capitalize truncate">
                {role?.replace('_', ' ')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium text-[var(--warm-gray)] hover:text-[var(--destructive)] hover:bg-[var(--destructive)]/10 transition-all duration-200 border border-transparent hover:border-[var(--destructive)]/20"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}
